import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OverlayOptions } from 'primeng/api';
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, from, isObservable, Observable, of } from 'rxjs';
import { ValueAccessorDirective } from '../../directives/value-accessor.directive';
import { formErrorMessage, getVisibleFormErrorTypes } from '../../helpers/helper';
import { CustomErrorMessage } from '../../models/custom-error-messages.model';

@Component({
  selector: 'pa-autocomplete',
  templateUrl: './pa-autocomplete.component.html',
  styleUrls: ['./pa-autocomplete.component.scss'],
  imports: [
    AutoCompleteModule,
    ReactiveFormsModule,
    TooltipModule,
    NgClass,
    NgIf,
    NgFor,
  ],
  standalone: true,
  hostDirectives: [ValueAccessorDirective],
})
export class PaAutocompleteComponent implements OnInit {
  @Input() label: string = undefined;
  @Input() placeholder: string = '';
  @Input() tooltip: string = null;
  @Input() hint: string = undefined;

  @Input()
  set suggestions(value: unknown[]) {
    this._suggestions = value ?? [];
    if (!this.filterMethod) this.filteredSuggestions = this._suggestions;
  }
  get suggestions(): unknown[] {
    return this._suggestions;
  }

  @Input() filterMethod?: (
    event: AutoCompleteCompleteEvent,
  ) =>
    | unknown[]
    | Observable<unknown[]>
    | Promise<unknown[]>
    | void;

  @Input() optionLabel: string = 'name';
  @Input() optionValue: string = 'id';
  @Input() showClear: boolean = true;
  @Input() customErrorMessages: CustomErrorMessage | null = null;

  @Output() completeMethod = new EventEmitter<AutoCompleteCompleteEvent>();
  @Output() select = new EventEmitter<AutoCompleteSelectEvent>();
  @Output() clear = new EventEmitter<void>();

  readonly valueAccessor = inject(ValueAccessorDirective<unknown>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly elRef = inject(ElementRef);
  private _suggestions: unknown[] = [];
  private lastRequestId = 0;

  isRequired = false;
  showErrors = false;
  errors: string[] = [];
  filteredSuggestions: unknown[] = [];

  readonly overlayOptions: OverlayOptions = {
    style: { 'max-width': '100%' },
    contentStyle: { 'min-width': 'inherit', width: '100%' },
  };

  get shouldShowClear(): boolean {
    return this.showClear && !this.isRequired;
  }

  get modalHost(): HTMLElement | null {
    const modal = this.elRef.nativeElement.closest('ion-modal');

    return modal ?? 'body';
  }

  ngOnInit(): void {
    const control = this.valueAccessor?.control;
    if (!control) return;

    this.isRequired = control.hasValidator(Validators.required);
    this.updateErrorState();

    control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.isRequired = control.hasValidator(Validators.required);
        this.updateErrorState();
      },
    );
  }

  handleComplete(event: AutoCompleteCompleteEvent): void {
    this.updateSuggestions(event);
    this.completeMethod.emit(event);
  }

  handleSelect(event: AutoCompleteSelectEvent): void {
    this.updateErrorState();
    this.select.emit(event);
  }

  handleBlur(): void {
    this.updateErrorState();
  }

  handleHide(): void {
    this.updateErrorState();
  }

  handleClear(): void {
    this.updateErrorState();
    this.filteredSuggestions = this.suggestions;
    this.clear.emit();
  }

  getErrorMessage(errorType: string): string {
    const error = this.valueAccessor?.control?.errors?.[errorType];
    return formErrorMessage(errorType, error, this.customErrorMessages);
  }

  private updateErrorState(): void {
    const control = this.valueAccessor?.control;
    if (!control) return;

    this.showErrors = (control.touched || control.dirty) && control.invalid;
    this.errors = getVisibleFormErrorTypes(control.errors, this.customErrorMessages);
  }

  private updateSuggestions(event: AutoCompleteCompleteEvent): void {
    const requestId = ++this.lastRequestId;
    const result = this.filterMethod?.(event);


    this.toObservable(result, event)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        if (requestId === this.lastRequestId) {

          console.log(items);

          this.filteredSuggestions = items ?? [];
          this.cdr.detectChanges();
        }
      });
  }

  private toObservable(
    result: unknown,
    event: AutoCompleteCompleteEvent,
  ): Observable<unknown[]> {
    if (Array.isArray(result)) return of(result);
    if (isObservable(result)) return result as Observable<unknown[]>;
    if (this.isPromise(result)) return from(result);
    return of(this.buildLocalSuggestions(event));
  }

  private isPromise(value: unknown): value is Promise<unknown[]> {
    return !!value && typeof (value as Promise<unknown[]>).then === 'function';
  }

  private buildLocalSuggestions(event: AutoCompleteCompleteEvent): unknown[] {
    const query = (event?.query ?? '').toLowerCase().trim();
    if (!query) return this.suggestions;

    return this.suggestions.filter((item) => {
      if (item == null) return false;
      if (typeof item === 'string' || typeof item === 'number') {
        return `${item}`.toLowerCase().includes(query);
      }
      const label = (item as Record<string, unknown>)[this.optionLabel];
      return `${label ?? ''}`.toLowerCase().includes(query);
    });
  }
}

